import React from 'react'
import PropTypes from 'prop-types'
import styled, { css, withTheme } from 'styled-components'
import { customPropTypes } from 'tools/react'
import {
  buttonStyles, buttonShapes, buttonRadiusValues, colors, colorConstants,
  dropShadows, dropShadowVariations, padding, paddingSizes
} from 'theme/default'
import { pixelise, darken, lighten, isLight } from 'tools/style'
import breakpoint from 'tools/styled-components-breakpoint'
import { H4 } from 'components/Typo'
import Loading from 'components/Loading'

const isColorReversed = (props) => props.secondary || props.buttonStyle === buttonStyles.outline
const getDarkestColor = (color) => darken(color, 25)
const getDarkColor = (color) => darken(color, 15)
const getLightColor = (color) => lighten(color, 15)

const getColor = (props) => isColorReversed(props) ? props.buttonColor : props.buttonTextColor
const getHoverColor = (props) => isColorReversed(props) ? getDarkColor(getColor(props)) : getColor(props)
const getBackgroundColor = (props) => isColorReversed(props) ? 'transparent' : props.buttonColor
const getHoverBackgroundColor = (props) => isLight(props.buttonColor)
  ? getDarkColor(getBackgroundColor(props))
  : getLightColor(getBackgroundColor(props))
const getRaisedColor = (props) => getDarkColor(props.buttonColor)
const getHoverRaisedColor = (props) => getDarkestColor(props.buttonColor)
const getBorder = (props) => isColorReversed(props) ? `2px solid ${getColor(props)}` : 'none'
const getHoverBorder = (props) => isColorReversed(props) ? `2px solid ${getHoverColor(props)}` : 'none'
const getBoxShadow = (props) => props.disabled
  ? 'none'
  : props.buttonStyle === buttonStyles.raised
    ? `0 4px 0 ${getRaisedColor(props)}`
    : dropShadows[dropShadowVariations.light]
const getHoverBoxShadow = props => props.disabled
  ? 'none'
  : props.buttonStyle === buttonStyles.raised
    ? `0 4px 0 ${getHoverRaisedColor(props)}`
    : dropShadows[dropShadowVariations.light]

export const defaultButtonStyle = {
  buttonShape: buttonShapes.round,
  buttonStyle: buttonStyles.solid,
  buttonTextColor: colorConstants.white,
  buttonColor: colors.brandPrimary,
  width: 'auto'
}

export const buttonColorStyle = css`
  color: ${getColor};
  background-color: ${getBackgroundColor};
  &:focus {
    color: ${getHoverColor};
    background-color: ${getHoverBackgroundColor};
  }

  ${breakpoint('desktop')`
    &:hover {
      color: ${getHoverColor};
      background-color: ${getHoverBackgroundColor};
    }
  `}
`

export const buttonStyle = css`
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  cursor: pointer;
  padding: ${padding[paddingSizes.small]} ${padding[paddingSizes.medium]};
  width: ${props => props.fullWidth ? '100%' : pixelise(props.width)};
  outline: none;
  pointer-events: ${props => props.disabled ? 'none' : 'auto'};
  ${props => props.disabled ? 'opacity: 0.5;' : ''}
  ${buttonColorStyle}
  border-radius: ${props => buttonRadiusValues[props.buttonShape]};
  box-shadow: ${getBoxShadow};
  border: ${getBorder};
  &:focus {
    box-shadow: ${getHoverBoxShadow};
    border: ${getHoverBorder};
  }

  ${breakpoint('desktop')`
    &:hover {
      box-shadow: ${getHoverBoxShadow};
      border: ${getHoverBorder};
    }
  `}
`

const Button = styled.button.attrs((props) => ({
  type: props.type || 'button'
}))`
  ${buttonStyle}
`
Button.propTypes = {
  className: PropTypes.string,
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  fullWidth: PropTypes.bool,
  secondary: PropTypes.bool,
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  buttonStyle: customPropTypes.oneOfObjectKeys(buttonStyles),
  buttonShape: customPropTypes.oneOfObjectKeys(buttonShapes),
  buttonTextColor: PropTypes.string,
  buttonColor: PropTypes.string
}
Button.defaultProps = {
  ...defaultButtonStyle
}

export const DefaultButtonText = styled(H4).attrs(() => ({
  normalised: true,
  bold: true
}))`
  color: inherit;
`

const ButtonComponent = ({ children, ButtonText, loading, ...props }) => {
  const LoadingElement = <Loading height='22px' width='22px' color={getColor(props)} />
  return (
    <Button {...props}>
      {typeof children === 'function'
        ? children({ loading, LoadingElement })
        : loading ? LoadingElement : <ButtonText>{children}</ButtonText>
      }
    </Button>
  )
}

ButtonComponent.propTypes = {
  children: PropTypes.oneOfType([PropTypes.element, PropTypes.string, PropTypes.func]),
  ButtonText: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  ...Button.propTypes
}
ButtonComponent.defaultProps = {
  ButtonText: DefaultButtonText,
  ...Button.defaultProps
}

export default withTheme(({
  theme: { buttonShape, buttonStyle, buttonTextColor, buttonColor } = {},
  ...props
}) => (
  <ButtonComponent
    buttonShape={buttonShape}
    buttonStyle={buttonStyle}
    buttonTextColor={buttonTextColor}
    buttonColor={buttonColor}
    {...props}
  />))
